// Whether coins can be sold (wave 9, docs/build/CONTRACTS.md "Selling"): money's public `GET /v1/config` says
// `"selling": true|false`. A missing field, an error or an unreachable money all read as false. With it false the
// site shows no money surface: no coin prices, tips, earnings, payouts or checkout pages, and paid store items
// leave the lists (their own page says "Not available yet"). Read before every page render, cached for a minute.
//
// The helpers below take `on` as an argument so both states can be tested; pages pass `selling()`.

import { api } from "./api.ts";

export const SELLING_TTL_MS = 60_000;

let on = false;
let at = Number.NEGATIVE_INFINITY;
let body: unknown = null;
let inflight: Promise<boolean> | null = null;

/** The last answer (false until money has said otherwise). */
export function selling(): boolean {
  return on;
}

/** Money's last `/v1/config` body (null when it couldn't be read), for pages that need more of it. */
export function moneyConfig(): unknown {
  return body;
}

/** For tests and for pages that learned the answer some other way. */
export function setSelling(value: boolean, when = Date.now(), raw: unknown = null): void {
  on = value;
  at = when;
  body = raw;
}

/** Only a literal `true` counts. */
export function parseSelling(raw: unknown): boolean {
  return !!raw && typeof raw === "object" && (raw as { selling?: unknown }).selling === true;
}

/** Ask money (at most once a minute). Never throws: any failure is false, cached like an answer. */
export function loadSelling(
  fetchConfig: () => Promise<unknown> = () => api<unknown>("money", "/config", { auth: false }),
  now: () => number = Date.now,
): Promise<boolean> {
  if (now() - at < SELLING_TTL_MS) return Promise.resolve(on);
  inflight ??= fetchConfig()
    .then(
      (raw) => setSelling(parseSelling(raw), now(), raw),
      () => setSelling(false, now(), null),
    )
    .then(() => on)
    .finally(() => (inflight = null));
  return inflight;
}

// ---- what each surface shows ---------------------------------------------------------------------------------

/** Paid items leave browse, search and home rows when not selling; free items are unchanged. */
export function visibleItems<T extends { price: number }>(list: T[], isOn: boolean): T[] {
  return isOn ? list : list.filter((a) => !(a.price > 0));
}

/** What a paid item's own page offers: its price, nothing (free), or "Not available yet". */
export type Offer = "free" | "price" | "unavailable";
export function itemOffer(price: number, isOn: boolean): Offer {
  return !(price > 0) ? "free" : isOn ? "price" : "unavailable";
}

export const NOT_AVAILABLE = "Not available yet";

/** The `/checkout/*` return pages exist only while selling; otherwise they send you home. */
export function checkoutRedirect(isOn: boolean): string | null {
  return isOn ? null : "/";
}
